import React, { useState } from 'react';
import { User, Mail, Lock, Phone, Eye, EyeOff, Smartphone, ChevronLeft, ShoppingBag, Store } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { useApp } from '../context/AppContext';
import { Screen } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
export function SignUpScreen() {
  const {
    navigate,
    resetTo,
    goBack
  } = useNavigation();
  const {
    setUser
  } = useApp();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupMethod, setSignupMethod] = useState<'email' | 'phone'>('email');
  const [accountType, setAccountType] = useState<'customer' | 'vendor'>('customer');
  const handleSignUp = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (accountType === 'vendor') {
        navigate(Screen.VENDOR_VERIFY);
      } else {
        navigate(Screen.OTP);
      }
    }, 1000);
  };
  const handleSocialSignup = (provider: string) => {
    setLoading(true);
    setTimeout(() => {
      setUser({
        id: 'u1',
        name: 'John Doe',
        email: 'john@example.com'
      });
      setLoading(false);
      resetTo(Screen.HOME);
    }, 1200);
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1391">
      {/* Branded Header */}
      <div className="bg-gradient-to-br from-primary to-primary-800 pt-12 pb-16 px-6 relative" data-id="element-1392">
        <button onClick={goBack} className="absolute top-12 left-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" data-id="element-1393">
          <ChevronLeft className="w-6 h-6" data-id="element-1394" />
        </button>

        <div className="flex flex-col items-center text-center mt-4" data-id="element-1395">
          <img src="https://cdn.magicpatterns.com/uploads/qwjBm9dydTfJXkZFWZ9RtX/JUMMALL_UPDATED_LOGO_(1).png" alt="JUMMALL Logo" className="w-32 h-auto object-contain mb-6 drop-shadow-md" data-id="element-1396" />
          <h1 className="text-2xl font-bold text-white mb-1" data-id="element-1397">Create Account</h1>
          <p className="text-primary-100 text-sm" data-id="element-1398">
            {accountType === 'vendor' ? 'Start selling on JUMMALL' : 'Join JUMMALL today'}
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-white rounded-t-[32px] -mt-6 px-6 pt-8 pb-6 flex flex-col shadow-[0_-8px_30px_rgba(0,0,0,0.1)]" data-id="element-1399">
        {/* Account Type Selector */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-5" data-id="element-1400">
          <button onClick={() => setAccountType('customer')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${accountType === 'customer' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`} data-id="element-1401">
            <ShoppingBag className="w-4 h-4" data-id="element-1402" />
            Customer
          </button>
          <button onClick={() => setAccountType('vendor')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${accountType === 'vendor' ? 'bg-white text-accent shadow-sm' : 'text-gray-500'}`} data-id="element-1403">
            <Store className="w-4 h-4" data-id="element-1404" />
            Vendor
          </button>
        </div>

        {/* Social Login Buttons */}
        <div className="flex flex-col gap-3 mb-5" data-id="element-1405">
          <button onClick={() => handleSocialSignup('Google')} className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 active:scale-[0.98] transition-all shadow-sm" data-id="element-1406">
            <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-1407">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" data-id="element-1408" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-1409" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-1410" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-1411" />
            </svg>
            <span className="text-sm font-semibold text-gray-700" data-id="element-1412">
              Continue with Google
            </span>
          </button>

          <div className="flex gap-3" data-id="element-1413">
            <button onClick={() => handleSocialSignup('Apple')} className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gray-900 hover:bg-gray-800 active:scale-[0.98] transition-all shadow-sm" data-id="element-1414">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor" data-id="element-1415">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-1416" />
              </svg>
              <span className="text-sm font-semibold text-white" data-id="element-1417">Apple</span>
            </button>

            <button onClick={() => handleSocialSignup('Facebook')} className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] active:scale-[0.98] transition-all shadow-sm" data-id="element-1418">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor" data-id="element-1419">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-1420" />
              </svg>
              <span className="text-sm font-semibold text-white" data-id="element-1421">Facebook</span>
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="my-4 flex items-center gap-4" data-id="element-1422">
          <div className="flex-1 h-px bg-gray-200" data-id="element-1423"></div>
          <span className="text-sm text-gray-400 font-medium" data-id="element-1424">
            or sign up with
          </span>
          <div className="flex-1 h-px bg-gray-200" data-id="element-1425"></div>
        </div>

        {/* Email / Phone Toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-5" data-id="element-1426">
          <button onClick={() => setSignupMethod('email')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${signupMethod === 'email' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`} data-id="element-1427">
            <Mail className="w-4 h-4" data-id="element-1428" />
            Email
          </button>
          <button onClick={() => setSignupMethod('phone')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${signupMethod === 'phone' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`} data-id="element-1429">
            <Smartphone className="w-4 h-4" data-id="element-1430" />
            Phone
          </button>
        </div>

        {/* Signup Form */}
        <motion.div key={`${signupMethod}-${accountType}`} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2
      }} className="flex flex-col gap-4 mb-5" data-id="element-1431">
          <Input label="Full Name" placeholder="John Doe" icon={<User className="w-5 h-5" data-id="element-1433" />} data-id="element-1432" />

          {accountType === 'vendor' && <Input label="Store Name" placeholder="e.g. Kingsley Electronics" icon={<Store className="w-5 h-5" data-id="element-1435" />} data-id="element-1434" />}

          {signupMethod === 'email' ? <Input label="Email Address" type="email" placeholder="john@example.com" icon={<Mail className="w-5 h-5" data-id="element-1437" />} data-id="element-1436" /> : <Input label="Phone Number" type="tel" placeholder="+234 800 000 0000" icon={<Phone className="w-5 h-5" data-id="element-1439" />} data-id="element-1438" />}

          <Input label="Password" type={showPassword ? 'text' : 'password'} placeholder="Create a password" icon={<Lock className="w-5 h-5" data-id="element-1441" />} rightIcon={<button type="button" onClick={() => setShowPassword(!showPassword)} data-id="element-1442">
                {showPassword ? <EyeOff className="w-5 h-5" data-id="element-1443" /> : <Eye className="w-5 h-5" data-id="element-1444" />}
              </button>} data-id="element-1440" />

          <div className="flex items-start gap-2 mt-1" data-id="element-1445">
            <input type="checkbox" id="terms" className="mt-1 rounded text-primary focus:ring-primary" data-id="element-1446" />
            <label htmlFor="terms" className="text-sm text-gray-500" data-id="element-1447">
              I agree to the{' '}
              <span className="text-primary font-medium" data-id="element-1448">
                {accountType === 'vendor' ? 'Seller Terms' : 'Terms of Service'}
              </span>{' '}
              and{' '}
              <span className="text-primary font-medium" data-id="element-1449">Privacy Policy</span>
            </label>
          </div>
        </motion.div>

        <Button fullWidth size="lg" onClick={handleSignUp} loading={loading} className="shadow-md" data-id="element-1450">
          {accountType === 'vendor' ? 'Create Seller Account' : 'Sign Up'}
        </Button>

        <div className="text-center mt-6 mt-auto" data-id="element-1451">
          <span className="text-gray-500 text-sm" data-id="element-1452">
            Already have an account?{' '}
          </span>
          <button onClick={() => navigate(Screen.LOGIN)} className="text-primary font-bold text-sm hover:underline" data-id="element-1453">
            Login
          </button>
        </div>
      </div>
    </motion.div>;
}